import {
  Badge,
  Box,
  Button,
  Container,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Image,
  Input,
  SimpleGrid,
  Stack,
  Text,
  Textarea,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { ArrowForwardIcon, CheckCircleIcon, EmailIcon, PhoneIcon } from "@chakra-ui/icons";
import { FaBrain, FaClock, FaMapMarkerAlt, FaProjectDiagram, FaRobot } from "react-icons/fa";
import { useState } from "react";
import emailjs from "emailjs-com";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { motion } from "framer-motion";
import Navbar from "../components/utilities/navbar";
import Footer from "../components/utilities/footer";
import contact from "../assets/contact.png";
import { theme } from "../components/utilities/theme";

const MotionContainer = motion.create(Container);

const contactPrompts = [
  "What are you trying to automate, launch, or improve?",
  "Do you already have data, APIs, a website, or an existing product?",
  "What timeline, budget range, or decision deadline should we know?",
];

export default function Contact() {
  const {
    contactPageBg: pageBg,
    contactInputBg: inputBg,
    contactTextColor: textColor,
    contactAccentColor: accentColor,
    errorColor,
    textSecondary,
    accentCyan,
    cardBg,
  } = theme.colors;
  const toast = useToast();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const phoneRegex = /^\d{10}$/;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePhoneChange = (value) => {
    setFormData({ ...formData, phone: value || "" });
  };

  const validate = () => {
    let err = {};
    if (!formData.name) err.name = "Name is required";
    if (!formData.email && !formData.phone)
      err.email = "Either email or phone is required";

    if (formData.phone) {
      const digits = formData.phone.replace(/\D/g, "");
      const lastTen = digits.slice(-10);
      if (lastTen.length !== 10) {
        err.phone = "Phone number must be exactly 10 digits excluding country code";
      } else if (!phoneRegex.test(lastTen)) {
        err.phone = "Invalid phone format";
      }
    }

    if (!formData.message) err.message = "Message is required";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    emailjs
      .send(
        "service_6btmh8l",
        "template_00nlixi",
        {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
        },
        "gaV2tAhSXcUTON74N"
      )
      .then(() => {
        if (formData.email?.trim()) {
          return emailjs.send(
            "service_6btmh8l",
            "template_0q8k6kh",
            {
              name: formData.name,
              email: formData.email,
              to_email: formData.email,
            },
            "gaV2tAhSXcUTON74N"
          );
        }
        return Promise.resolve();
      })
      .then(() => {
        toast({
          title: "Message Sent",
          description: "Thank you for contacting us!",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
        setFormData({ name: "", email: "", message: "", phone: "" });
        setErrors({});
      })
      .catch(() => {
        toast({
          title: "Error",
          description: "Failed to send message. Try again later.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      });
  };

  return (
    <Box bg={pageBg} color={textColor} minH="100vh">
      <Navbar />

      <MotionContainer
        id="main-content"
        as="main"
        maxW="1360px"
        px={{ base: 4, md: 8 }}
        py={{ base: 12, md: 16 }}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
      >
        <Stack direction={{ base: "column", lg: "row" }} spacing={10} align="center" mb={12}>
          <Box flex="1.05">
            <Badge
              bg="rgba(0, 255, 208, 0.1)"
              color={accentCyan}
              border="1px solid rgba(0, 255, 208, 0.28)"
              borderRadius="full"
              px={4}
              py={1}
              mb={5}
            >
              Contact Orvitrix
            </Badge>
            <Heading as="h1" fontSize={{ base: "3xl", md: "5xl" }} lineHeight="1.08" mb={5}>
              Tell us what you want to build, automate, or improve.
            </Heading>
            <Text color={textSecondary} fontSize={{ base: "md", md: "xl" }} lineHeight="1.8">
              Share the workflow, product idea, integration challenge, or growth
              goal. We will help you shape a practical next step.
            </Text>

            <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4} mt={7}>
              {[
                { icon: FaRobot, label: "Assistants" },
                { icon: FaProjectDiagram, label: "Apps and APIs" },
                { icon: FaBrain, label: "Strategy review" },
              ].map((item) => (
                <Box
                  key={item.label}
                  bg={cardBg}
                  border="1px solid rgba(255, 255, 255, 0.12)"
                  borderRadius="xl"
                  p={5}
                >
                  <Icon as={item.icon} color={accentCyan} boxSize={6} mb={3} />
                  <Text fontWeight="bold">{item.label}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>

          <Box
            flex="0.95"
            bg="rgba(15, 23, 42, 0.78)"
            border="1px solid rgba(255, 255, 255, 0.12)"
            borderRadius="xl"
            p={{ base: 6, md: 7 }}
            w="100%"
          >
            <Heading size="md" mb={5}>
              Helpful context to include
            </Heading>
            <VStack align="stretch" spacing={3}>
              {contactPrompts.map((prompt) => (
                <HStack key={prompt} align="start" spacing={3}>
                  <CheckCircleIcon color={accentColor} mt="3px" />
                  <Text color={textSecondary}>{prompt}</Text>
                </HStack>
              ))}
            </VStack>
          </Box>
        </Stack>

        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={8} alignItems="start">
          <Box
            as="form"
            onSubmit={handleSubmit}
            bg="rgba(15, 23, 42, 0.78)"
            border="1px solid rgba(255, 255, 255, 0.12)"
            borderRadius="xl"
            p={{ base: 6, md: 7 }}
          >
            <Stack spacing={5}>
              <FormControl isInvalid={!!errors.name} isRequired>
                <FormLabel>Name</FormLabel>
                <Input
                  placeholder="Your name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  bg={inputBg}
                  size="lg"
                  borderColor={errors.name ? errorColor : "rgba(255, 255, 255, 0.16)"}
                  _placeholder={{ color: "rgba(226, 232, 240, 0.5)" }}
                  _hover={{ borderColor: accentColor }}
                  _focus={{ borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` }}
                />
                <FormErrorMessage>{errors.name}</FormErrorMessage>
              </FormControl>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <FormControl isInvalid={!!errors.email}>
                  <FormLabel>Email</FormLabel>
                  <Input
                    placeholder="you@company.com"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    bg={inputBg}
                    size="lg"
                    borderColor={errors.email ? errorColor : "rgba(255, 255, 255, 0.16)"}
                    _placeholder={{ color: "rgba(226, 232, 240, 0.5)" }}
                    _hover={{ borderColor: accentColor }}
                    _focus={{ borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` }}
                  />
                  <FormErrorMessage>{errors.email}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.phone}>
                  <FormLabel>Phone</FormLabel>
                  <Box
                    sx={{
                      ".PhoneInput": { display: "flex", alignItems: "center", width: "100%" },
                      ".PhoneInputCountry": {
                        background: inputBg,
                        borderRadius: "8px 0 0 8px",
                        padding: "8px 10px",
                        border: errors.phone ? `1px solid ${errorColor}` : "1px solid rgba(255,255,255,0.16)",
                        borderRight: "none",
                        color: textColor,
                      },
                      ".PhoneInputCountrySelect": { background: inputBg, color: textColor, border: "none" },
                      ".PhoneInputCountrySelect option": { background: inputBg, color: textColor },
                      ".PhoneInputInput": {
                        width: "100%",
                        background: inputBg,
                        padding: "12px",
                        borderRadius: "0 8px 8px 0",
                        border: errors.phone ? `1px solid ${errorColor}` : "1px solid rgba(255,255,255,0.16)",
                        color: textColor,
                        fontSize: "16px",
                        outline: "none",
                      },
                      ".PhoneInputInput:focus": {
                        borderColor: accentColor,
                        boxShadow: `0 0 0 1px ${accentColor}`,
                      },
                    }}
                  >
                    <PhoneInput
                      international
                      defaultCountry="IN"
                      value={formData.phone}
                      onChange={handlePhoneChange}
                      name="phone"
                    />
                  </Box>
                  <FormErrorMessage>{errors.phone}</FormErrorMessage>
                </FormControl>
              </SimpleGrid>

              <FormControl isInvalid={!!errors.message} isRequired>
                <FormLabel>Project or idea</FormLabel>
                <Textarea
                  placeholder="Tell us about the goal, workflow, users, data, timeline, or constraints."
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={7}
                  bg={inputBg}
                  size="lg"
                  borderColor={errors.message ? errorColor : "rgba(255, 255, 255, 0.16)"}
                  _placeholder={{ color: "rgba(226, 232, 240, 0.5)" }}
                  _hover={{ borderColor: accentColor }}
                  _focus={{ borderColor: accentColor, boxShadow: `0 0 0 1px ${accentColor}` }}
                />
                <FormErrorMessage>{errors.message}</FormErrorMessage>
              </FormControl>

              <Button
                size="lg"
                type="submit"
                alignSelf={{ base: "stretch", sm: "flex-start" }}
                bg={accentColor}
                color="white"
                rightIcon={<ArrowForwardIcon />}
                borderRadius="lg"
                _hover={{ bg: "#fb923c", transform: "translateY(-2px)" }}
              >
                Send Message
              </Button>
            </Stack>
          </Box>

          <Stack spacing={5}>
            <Box
              bg={cardBg}
              border="1px solid rgba(255, 255, 255, 0.12)"
              borderRadius="xl"
              p={6}
            >
              <Heading size="md" mb={4}>
                Reach us directly
              </Heading>
              <VStack align="stretch" spacing={4}>
                <HStack align="start" spacing={3}>
                  <EmailIcon color={accentColor} mt="3px" />
                  <Box>
                    <Text fontWeight="bold">Email</Text>
                    <Text color={textSecondary}>info@orvitrix.com</Text>
                  </Box>
                </HStack>
                <HStack align="start" spacing={3}>
                  <PhoneIcon color={accentColor} mt="3px" />
                  <Box>
                    <Text fontWeight="bold">Phone</Text>
                    <Text color={textSecondary}>9901975001</Text>
                  </Box>
                </HStack>
                <HStack align="start" spacing={3}>
                  <Icon as={FaMapMarkerAlt} color={accentColor} mt="3px" />
                  <Box>
                    <Text fontWeight="bold">Location</Text>
                    <Text color={textSecondary}>Delhi, India</Text>
                  </Box>
                </HStack>
                <HStack align="start" spacing={3}>
                  <Icon as={FaClock} color={accentColor} mt="3px" />
                  <Box>
                    <Text fontWeight="bold">Best for</Text>
                    <Text color={textSecondary}>Product MVPs, automation, integrations, and technical roadmaps.</Text>
                  </Box>
                </HStack>
              </VStack>
            </Box>

            <Box display={{ base: "none", lg: "block" }}>
              <Image
                src={contact}
                alt="Contact Orvitrix"
                borderRadius="xl"
                border="1px solid rgba(255, 255, 255, 0.12)"
                boxShadow="0 18px 44px rgba(0, 0, 0, 0.24)"
                maxH="420px"
                objectFit="contain"
                mx="auto"
              />
            </Box>
          </Stack>
        </SimpleGrid>
      </MotionContainer>

      <Footer />
    </Box>
  );
}
